import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();

      // Wenn automatisch geöffnet wird, auch hier versuchen
      this.#triggerBssCartRepriceOnOpen();
    }
  };

  open() {
    this.showDialog();

    // WICHTIG: Drawer rendert async -> trigger delayed / mehrfach
    this.#triggerBssCartRepriceOnOpen();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  // ----------------------------
  // NEU: Reprice Trigger on Open
  // ----------------------------
  #triggerBssCartRepriceOnOpen = () => {
    // mehrfach versuchen, weil Horizon in Wellen rendert
    this.#ensureCartReprice();
    setTimeout(() => this.#ensureCartReprice(), 200);
    setTimeout(() => this.#ensureCartReprice(), 600);
  };

  #ensureCartReprice = async () => {
    const KEY = 'horizon_bss_b2b_reprice_done';
    if (sessionStorage.getItem(KEY) === '1') return;

    // Optional: Nur triggern wenn B2B (so wie du es im Markup setzt)
    // Wenn du das Flag im Drawer noch nicht hast, kommentier den Block aus.
    const isB2b = document.querySelector('.cart-drawer[data-is-b2b="true"]');
    if (!isB2b) return;

    // Warten bis Cart DOM da ist (BSS Nodes oder cart rows)
    const ready = await this.#waitFor(() =>
      document.querySelector('[bss-b2b-cart-item-key]') ||
      document.querySelector('[bss-b2b-final-line-price]') ||
      document.querySelector('.cart-drawer')
    , 1200);

    if (!ready) return;

    // sections id wie in deinem Payload (header section)
    const headerSection =
      document.querySelector('[id^="sections--"][id$="__header_section"]') ||
      document.querySelector('[data-section-id^="sections--"][data-section-id$="__header_section"]');

    const sections =
      headerSection?.getAttribute('id') ||
      headerSection?.getAttribute('data-section-id') ||
      '';

    // sections_url wie Horizon: aktuelle Produktseite (wie bei dir im Payload)
    const sections_url = window.location.pathname + window.location.search;

    try {
      // cart.js holen für "no-op" quantity
      const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!cartRes.ok) return;
      const cart = await cartRes.json();
      if (!cart?.items?.length) return;

      // WICHTIG: Horizon nutzt line (1-based) + quantity
      const line = 1;
      const quantity = cart.items[0].quantity;

      // exakt wie dein Network-Payload, nur quantity bleibt gleich
      const payload = { line, quantity, sections, sections_url };

      // Debug (kannst du nach Erfolg rausnehmen)
      console.debug('[BSS/B2B] trigger /cart/change payload', payload);

      const res = await fetch('/cart/change', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      });

      console.debug('[BSS/B2B] /cart/change status', res.status);

      if (!res.ok) return;

      sessionStorage.setItem(KEY, '1');
    } catch (e) {
      console.debug('[BSS/B2B] trigger failed', e);
    }
  };

  #waitFor = (fn, timeoutMs = 1000) => {
    const start = Date.now();
    return new Promise((resolve) => {
      const tick = () => {
        try {
          if (fn()) return resolve(true);
        } catch (_) {}
        if (Date.now() - start > timeoutMs) return resolve(false);
        requestAnimationFrame(tick);
      };
      tick();
    });
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}